import { DatePipe } from "@angular/common";
import { Component, computed, inject, input, output, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { NgSelectModule } from "@ng-select/ng-select";
import { TranslatePipe } from "@ngx-translate/core";

import { PercentageValuePipe, RoundValuePipe, SafeHtmlPipe } from "../../../../core/pipes";
import { AdaaHelper } from "../../../../core/utils";
import { Constants } from "../../../constants/constants";
import { AdaaBoolean, CellType } from "../../../constants/enums";
import {
  ParameterCatalog,
  TableInputChangedOutput,
  TableInputs,
  TableValueListOutput,
  ValueText,
} from "../../../models";
import { LanguageService } from "../../../services/language-service";
import { TranslationComponent } from "../../general/translation/translation";
import { DataTableCellSpecialComponent } from "../data-table-cell-special/data-table-cell-special";

@Component({
  selector: "adaa-data-table-cell",
  imports: [
    TranslatePipe,
    PercentageValuePipe,
    DatePipe,
    SafeHtmlPipe,
    RoundValuePipe,
    DataTableCellSpecialComponent,
    TranslationComponent,
    NgSelectModule,
    FormsModule,
  ],
  templateUrl: "./data-table-cell.html",
  styleUrl: "./data-table-cell.scss",
})
export class DataTableCellComponent {
  readonly languageService = inject(LanguageService);

  configKey = input<string>("");
  column = input.required<ParameterCatalog>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data = input<any>();
  tableInputs = input<TableInputs[]>([]);
  inputValues = input<TableValueListOutput[]>([]);
  rowKeyField = input<string>("id");

  inputChanged = output<TableInputChangedOutput>();

  translatedTextMap = signal<{ [key: string]: string } | undefined>(undefined);
  CellType = CellType;
  Constants = Constants;
  AdaaBoolean = AdaaBoolean;
  AdaaHelper = AdaaHelper;

  isCheckboxChecked = computed<boolean>(() => {
    const keyField = this.rowKeyField() || "id";
    const rowKey = this.data()?.[keyField] ?? this.data()?.id;
    const override = (this.inputValues() ?? []).find(
      (e) => e?.fieldName === this.column().fieldName && (e?.data?.[keyField] ?? e?.data?.id) === rowKey
    );
    if (override) {
      const input = (this.tableInputs() ?? []).find((e) => e.fieldName === this.column().fieldName);
      const mode = input?.checkboxMode ?? "trueFalse";
      if (mode === "yesNo") {
        return override.value === "Y";
      }
      // trueFalse mode: backend sometimes uses 1/0, boolean, or "1"/"0"
      return override.value === true || override.value === 1 || override.value === "1";
    }

    const input = (this.tableInputs() ?? []).find((e) => e.fieldName === this.column().fieldName);
    const mode = input?.checkboxMode ?? "trueFalse";

    const value = this.data()?.[this.column().fieldName];
    if (mode === "yesNo") {
      return value === "Y";
    }
    return value === true || value === 1 || value === "1";
  });

  isInputDisabled = computed<boolean>(() => {
    const tableInputs = this.tableInputs();
    const data = this.data();
    const column = this.column();

    if (tableInputs) {
      const input = tableInputs.find((e) => e.fieldName === column.fieldName);
      if (input?.disableRules) {
        let disabled = false;

        for (let index = 0; index < input.disableRules.length; index++) {
          const rule = input.disableRules[index];

          if (rule.operator === "equal") {
            if (data[rule.propertyName] === rule.compareWith) {
              disabled = true;
            }
          } else if (rule.operator === "not-equal") {
            disabled = true;
            if (data[rule.propertyName] === rule.compareWith) {
              disabled = false;
              break;
            }
          } else if (rule.operator === "notNull") {
            disabled = true;
            if (!AdaaHelper.isDefined(data[rule.propertyName])) {
              disabled = false;
            }
          } else if (rule.operator === "null") {
            disabled = true;
            if (AdaaHelper.isDefined(data[rule.propertyName])) {
              disabled = false;
            }
          }
        }

        return disabled;
      }

      return false;
    } else return data[column.fieldName + "Active"] ? data[column.fieldName + "Active"] !== "Y" : false;
  });

  getDdlOptions = computed<ValueText[]>(() => {
    const input = (this.tableInputs() ?? []).find((e) => e.fieldName === this.column().fieldName);

    return input?.options ?? [];
  });

  isClearable = computed<boolean>(() => {
    const input = (this.tableInputs() ?? []).find((e) => e.fieldName === this.column().fieldName);
    return input?.isClearable ?? false;
  });

  public getPercentageColor(): string {
    const data = this.data();
    const column = this.column();
    const configKey = this.configKey();

    //Milestone Table is using the onTimeProgressColor instead of progressColor
    if (
      configKey === Constants.MAIN_TABLE_LIST_CONF_KEY.PROJECT_MILESTONES_CONF_LIST ||
      configKey === Constants.MAIN_TABLE_LIST_CONF_KEY.NATIONAL_PROJECTS_CONF_LIST
    ) {
      if (AdaaHelper.isDefined(data[column.fieldName + "Color"])) {
        return Constants.COLOR_CODES[data["onTimeProgressColor"]];
      }
    }

    if (AdaaHelper.isDefined(data[column.fieldName + "Color"])) {
      return Constants.COLOR_CODES[data[column.fieldName + "Color"]];
    } else if (AdaaHelper.isDefined(data[column.fieldName + "Evaluation"])) {
      return Constants.COLOR_CODES[data[column.fieldName + "Evaluation"]];
    } else {
      if (AdaaHelper.isDefined(data["evaluationPerformance"])) {
        return Constants.COLOR_CODES[data["evaluationPerformance"]];
      } else if (AdaaHelper.isDefined(data["performanceEvaluation"])) {
        return Constants.COLOR_CODES[data["performanceEvaluation"]];
      } else if (AdaaHelper.isDefined(data["evaluationScore"])) {
        return Constants.COLOR_CODES[data["evaluationScore"]];
      } else if (AdaaHelper.isDefined(data["performance"])) {
        return Constants.COLOR_CODES[data["evaluation"]];
      } else {
        return Constants.COLOR_CODES[data["evaluationPerformance"]];
      }
    }
  }

  public checkboxValueChanged(event: Event, fieldName: string) {
    const target = event.target as HTMLInputElement;
    const tableInputs = this.tableInputs();

    if (tableInputs) {
      const input = tableInputs.find((e) => e.fieldName === this.column().fieldName);
      if (input) {
        input.checkboxMode === "trueFalse"
          ? this.inputChanged.emit({ value: target.checked ? 1 : 0, data: this.data(), fieldName })
          : this.inputChanged.emit({
              value: target.checked ? AdaaBoolean.Y : AdaaBoolean.N,
              data: this.data(),
              fieldName,
            });

        return;
      }
    }
    this.inputChanged.emit({ value: target.checked ? 1 : 0, data: this.data(), fieldName });
  }

  public getNumberValue(fieldName: string) {
    return this.data()[fieldName] as number;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public dropdownValueChanged(value: any, fieldName: string) {
    this.inputChanged.emit({ value: value, data: this.data(), fieldName });
  }

  public onTranslationDone(type: string, translated: string): void {
    this.translatedTextMap.update((map) => ({ ...(map ?? {}), [type]: translated }));
  }
}
